import { useEffect, useState } from 'react';
import { getData, patchData } from '../backend';
import { useModal } from '../contexts';
import Modal from './Modal';
import useSWR, { KeyedMutator } from "swr";
import SmallTextInputField from './SmallTextInputField';
import { Workflow } from '../interfaces';
import SmallSelectField from './SmallSelectField';
import WorkflowModalHubspotPropertiesList from './WorkflowModalHubspotPropertiesList';

interface Property {
  description: string;
  hubspotDefined: boolean;
  label: string;
  name: string;
  type: "bool" | "enumeration" | "date" | "datetime" | "string" | "number";
}

interface Props {
  objectTypeId: string;
  workflow: Workflow;
  refreshWorkflow: KeyedMutator<Workflow>;
  onClose: () => void;
}

function WorkflowModalHubspotPropertiesDetail(props: Props) {
  const { openModal } = useModal();

  const [search, setSearch] = useState("");
  const [checkedProperties, setCheckedProperties] = useState(props.workflow.source?.hubspotConfig?.hubspotProperties ?? []);

  const [sortingMap, setSortingMap] = useState<{ [key: string]: { field: string, desc: boolean } }>({});

  const [saveState, setSaveState] = useState<"INITIAL" | "SAVING" | "SAVED">("INITIAL");

  const { data: objectTypeData, isLoading } = useSWR(`/hubspot/object-types/${props.objectTypeId}/`, getData);

  const [preventClose, setPreventClose] = useState(false);

  useEffect(() => {
    if (saveState !== "SAVING")
      return setPreventClose(false);

    setPreventClose(true);
    const timer = setTimeout(() => setPreventClose(false), 30_000);

    return () => {
      clearTimeout(timer);
      setPreventClose(false);
    };

  }, [saveState]);

  useEffect(() => {
    setSortingMap(props.workflow.source?.hubspotConfig?.hubspotAssociatedObjectSorting?.reduce((acc: any, current) => {
      const [obj, fieldWithOrder] = current.split(".");
      const desc = fieldWithOrder.endsWith("-");

      const field = fieldWithOrder.replace("-", "");

      acc[obj] = { field, desc };
      return acc;
    }, {}) ?? {});
  }, [props.workflow.source?.hubspotConfig?.hubspotAssociatedObjectSorting]);

  const filteredProperties = (objectTypeData?.properties ?? []).filter((property: any) => {
    if (search === "") return true;
    return property.label.toLowerCase().includes(search.toLowerCase());

  }).sort((a: any, b: any) => {
    if (a.label < b.label) return -1;
    if (a.label > b.label) return 1;
    return 0;
  });

  function isPropertyChecked(property: Property) {
    const propertyName = `${props.objectTypeId}.${property.name}`;
    return checkedProperties.includes(propertyName);
  }

  function handleToggleProperty(property: Property, value?: boolean) {
    setSaveState("INITIAL");

    if (value === undefined)
      value = !isPropertyChecked(property);

    const propertyName = `${props.objectTypeId}.${property.name}`;

    if (value)
      setCheckedProperties(current => [...current, propertyName]);
    else
      setCheckedProperties(current => current.filter((name) => name !== propertyName));
  }

  function handleUpdateHubspotSourceConfig() {
    setSaveState("SAVING");

    const hubspotAssociatedObjectSorting = Object.entries(sortingMap).map(([objectType, { field, desc }]) => `${objectType}.${field}${desc ? "-" : ""}`);

    patchData(`/hubspot/config/${props.workflow.source?.hubspotConfig?.id}/`, {
      hubspotProperties: Array.from(new Set(checkedProperties)),
      hubspotAssociatedObjectSorting
    })
      .then(() => { props.refreshWorkflow().then(returnToSelectModal) })
      .catch(console.error)
      .finally(() => setSaveState("SAVED"));
  }

  function updateSortingOrder(field: string, desc: boolean) {
    setSaveState("INITIAL");

    const newSortingMap = { ...sortingMap, [props.objectTypeId]: { field, desc } };
    setSortingMap(newSortingMap);
  }

  const { field, desc } = sortingMap[props.objectTypeId] ?? { field: "", desc: false };

  function returnToSelectModal(workflow?: Workflow) {
    openModal(<WorkflowModalHubspotPropertiesList workflow={workflow ?? props.workflow} refreshWorkflow={props.refreshWorkflow} onClose={props.onClose} />);
  }

  return (
    <Modal
      title={objectTypeData?.labels.plural ?? "Properties"}
      subtitle="Select properties from this object you would like to use in your workflow."
      secondaryButtonProps={{ text: "Back", onClick: () => returnToSelectModal(), disabled: saveState === "SAVING" }}
      primaryButtonProps={isLoading ? undefined : { text: { "INITIAL": "Save", "SAVING": "Saving...", "SAVED": "Saved" }[saveState], onClick: handleUpdateHubspotSourceConfig, disabled: saveState !== "INITIAL" }}
      preventClose={preventClose}
    >
      {isLoading &&
        <div className='flex items-center justify-center w-full h-[200px]'>
          Loading...
        </div>
      }

      {!isLoading &&
        <div className='flex flex-col'>
          <div className='flex items-center justify-between w-full p-2 border-b'>
            <SmallTextInputField
              value={search}
              onChange={setSearch}
              placeholder="Search properties..."
              className="w-full"
            />
          </div>
          <div className='flex flex-col overflow-y-scroll h-[320px] gap-2 pr-8'>
            {filteredProperties.map((property: any) => (
              <div key={property.name} className="flex items-center justify-between p-2 border-b last:border-0 cursor-pointer" onClick={() => handleToggleProperty(property)}>
                <div className="flex flex-col pr-2">
                  <span className="text-sm font-semibold">{property.label}</span>
                  <span className="text-xs text-gray-400">{property.description}</span>
                </div>
                <input
                  type="checkbox"
                  checked={isPropertyChecked(property)}
                  onChange={(e) => { handleToggleProperty(property, e.currentTarget.checked); e.stopPropagation() }}
                  disabled={saveState === "SAVING"}
                />
              </div>
            ))}
          </div>
          <div className='flex items-baseline rounded p-2 gap-2 text-sm border-t '>
            Sort associated {objectTypeData?.labels.plural.toLocaleLowerCase()} by
            <SmallSelectField label="Field" value={field} onChange={(value) => updateSortingOrder(value, desc)} className='w-56' disabled={saveState === "SAVING"}>
              <option value='' disabled>Select field (optional)</option>
              {objectTypeData?.properties.map((property: { name: string, label: string }) =>
                <option key={property.name} value={property.name}>{property.label}</option>
              )}
            </SmallSelectField>
            in
            <SmallSelectField label="Order" value={desc ? "-" : "+"} onChange={(value) => updateSortingOrder(field, value === "-")} className='w-40' disabled={!field || saveState === "SAVING"}>
              <option value="+">Ascending</option>
              <option value='-'>Descending</option>
            </SmallSelectField>
            order
          </div>
        </div>
      }
    </Modal>
  );
}

export default WorkflowModalHubspotPropertiesDetail;
